/**
 * useClientFleet - Fetch aircraft belonging to a client at a specific location
 *
 * Returns aircraft with their allocation and contract status for
 * displaying the "Client Fleet at Location" section in the Client tab.
 */

import { useQuery } from '@tanstack/react-query'
import { supabase } from '@/lib/supabase'

interface ClientFleetAircraft {
  id: string
  tailNumber: string
  typeName: string | null
  areaName: string | null
  unitCode: string | null
  contractStatus: 'lease' | 'transient' | 'none'
}

interface UseClientFleetResult {
  aircraft: ClientFleetAircraft[]
  isLoading: boolean
}

export function useClientFleet(
  clientId: string | null,
  locationId: string | null
): UseClientFleetResult {
  const { data, isLoading } = useQuery({
    queryKey: ['client-fleet', clientId, locationId],
    queryFn: async () => {
      if (!clientId || !locationId) return []

      // Query aircraft owned by this client that have allocations at this location
      const { data: rows, error } = await supabase
        .from('aircraft')
        .select(`
          id,
          tail_number,
          aircraft_types!aircraft_type_id (
            name
          ),
          allocations!inner (
            id,
            status,
            areas!area_id (
              name
            ),
            units!unit_id (
              unit_code
            ),
            lease_contract_id,
            transient_stay_id
          )
        `)
        .eq('owner_client_id', clientId)
        .eq('allocations.location_id', locationId)
        .in('allocations.status', ['active', 'reserved'])
        .is('deleted_at', null)
        .is('allocations.deleted_at', null)
        .limit(10)

      if (error) throw error
      if (!rows) return []

      return rows.map((row: any) => {
        const allocation = Array.isArray(row.allocations)
          ? row.allocations[0]
          : row.allocations

        let contractStatus: 'lease' | 'transient' | 'none' = 'none'
        if (allocation?.lease_contract_id) contractStatus = 'lease'
        else if (allocation?.transient_stay_id) contractStatus = 'transient'

        const area = allocation?.areas
        const unit = allocation?.units

        return {
          id: row.id,
          tailNumber: row.tail_number,
          typeName: row.aircraft_types?.name ?? null,
          areaName: typeof area === 'object' && area !== null ? area.name : null,
          unitCode: typeof unit === 'object' && unit !== null ? unit.unit_code : null,
          contractStatus,
        } as ClientFleetAircraft
      })
    },
    enabled: !!clientId && !!locationId,
    staleTime: 60_000,
  })

  return {
    aircraft: data ?? [],
    isLoading,
  }
}
